import React from 'react';
import { useTranslation } from 'react-i18next';
import './AudioBitrateInput.scss';
import { Option } from '../../../common/interfaces';
import { Dropdown } from '../../FormComponents/Dropdown/Dropdown';
import { AudioBitrateEnum } from '../../../types/enums/AudioBitrateEnum';
import useProject from '../../../hooks/useProject';
import Help from '../../Help/Help';

const AudioBitrateInput = () => {
  const { t } = useTranslation();
  const { updateEncoder, updateStream, project } = useProject();
  const options: Option[] = [];

  for (const key in AudioBitrateEnum) {
    if (isNaN(Number(key))) {
      options.push({
        text: `${key.split(/(?=[A-Z])/).join(' ')}`,
        value: `${AudioBitrateEnum[key as keyof typeof AudioBitrateEnum]}`,
      });
    }
  }

  const onChange = (option: Option) => {
    if (project.stream.sourceLanguageIsoCode === project.encoder.languageIsoCode) {
      updateStream({
        transcoderAudioBitrate: parseInt(option.value),
      });
    } else {
      updateEncoder(project.encoder, {
        transcoderAudioBitrate: parseInt(option.value),
      });
    }
  };

  return (
    <div className="audio-bitrate-input">
      <div className="input-title">
        {t('producer.inputs.audio-bitrate.title')}
        <Help
          title="Audio Bitrate"
          text="<b>32 kbps:</b> Very low quality, minimal bandwidth usage, suitable for speech-only streams.<br/><br/><b>64 kbps:</b> Low quality, suitable for voice content with limited bandwidth.<br/><br/><b>128 kbps:</b> Standard quality, good for general audio streams.<br/><br/><b>192 kbps:</b> Improved quality, suitable for music or higher-fidelity audio.<br/><br/><b>256 kbps:</b> High-quality audio, good for professional streaming.<br/><br/><b>320 kbps:</b> Very high-quality audio, near CD quality.<br/><br/><b>512 kbps:</b> Excellent quality, suitable for high-fidelity music streaming.<br/><br/><b>768 kbps:</b> Superior quality for professional audio applications.<br/><br/><b>1024 kbps:</b> Maximum quality, best for studio-grade audio streaming."
        />
      </div>
      <Dropdown
        value={project.encoder.transcoderAudioBitrate.toString()}
        options={options}
        onChange={onChange}
        placeholderKey="producer.inputs.audio-bitrate.placeholder"
        readonly={false}
      />
      <div className="input-component-footer">{t('producer.inputs.audio-bitrate.footer')}</div>
    </div>
  );
};

export default React.memo(AudioBitrateInput);
